import { FunnelSettingsServiceInterface } from 'core/common/services/funnel-settings/interfaces';
import { StorageInterface } from 'core/common/services/storage';
import { FunnelStepService } from '../../interfaces';

type StoredStepPath = {
  funnel: string;
  screens: Array<string>;
};

enum StepResetReason {
  RESTORED = 'RESTORED',
  NO_STORED_PATH = 'NO_STORED_PATH',
  SCOPE_MISMATCH = 'SCOPE_MISMATCH',
  STORAGE_UNAVAILABLE = 'STORAGE_UNAVAILABLE',
}

export class FunnelStepClientService implements FunnelStepService {
  private readonly storageKey = 'funnelStepPath';
  private stepPath: StoredStepPath | null = null;
  private position = 0;
  private resetReason: StepResetReason | null = null;

  constructor(
    private readonly funnelSettingsService: FunnelSettingsServiceInterface,
    private readonly storage?: StorageInterface,
  ) {}

  updateScreensPath(screenId: string) {
    const screens = this.getScreens();
    const visitedIndex = screens.indexOf(screenId);
    const isNewScreen = visitedIndex === -1;
    const updatedScreens = isNewScreen ? [...screens, screenId] : screens;

    this.position = isNewScreen ? updatedScreens.length : visitedIndex + 1;
    this.stepPath = { funnel: this.getFunnelName(), screens: updatedScreens };

    if (isNewScreen) {
      this.save(this.stepPath);
    }
  }

  getStepNumber() {
    return this.position;
  }

  consumeResetReason() {
    const reason = this.resetReason;
    this.resetReason = null;

    return reason;
  }

  private getScreens(): Array<string> {
    if (this.stepPath && this.isCurrentFunnel(this.stepPath)) return this.stepPath.screens;

    return this.restore();
  }

  private restore(): Array<string> {
    const storedStepPath = this.read();

    if (!storedStepPath) return [];

    if (!this.isCurrentFunnel(storedStepPath)) {
      this.resetReason = StepResetReason.SCOPE_MISMATCH;

      return [];
    }

    this.resetReason = StepResetReason.RESTORED;

    return storedStepPath.screens;
  }

  private isCurrentFunnel(stepPath: StoredStepPath): boolean {
    return stepPath.funnel === this.getFunnelName();
  }

  private read(): StoredStepPath | null {
    if (!this.storage) {
      this.resetReason = StepResetReason.STORAGE_UNAVAILABLE;

      return null;
    }

    try {
      const storedStepPath = this.storage.get<StoredStepPath>(this.storageKey);

      if (!storedStepPath) {
        this.resetReason = StepResetReason.NO_STORED_PATH;

        return null;
      }

      return storedStepPath;
    } catch {
      this.resetReason = StepResetReason.STORAGE_UNAVAILABLE;

      return null;
    }
  }

  private save(stepPath: StoredStepPath) {
    if (!this.storage) return;

    try {
      this.storage.set(this.storageKey, stepPath);
    } catch {
      return;
    }
  }

  private getFunnelName(): string {
    return this.funnelSettingsService.getFunnelName();
  }
}
